import React, { useMemo, useState } from 'react'

import {
  useDashboardGroupsLoading,
  useFavoriteDashboardGroups,
  useRecentDashboardGroups
} from 'redux/hooks/DashboardGroups'
import { useFavoriteReports, useRecentReports } from 'redux/hooks/Reports'

import { Context } from 'types/GlobalUserEvents'
import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Emoji from 'components_new/atoms/Emoji'
import List from 'components_new/atoms/List'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import StatusBox from 'components_new/molecules/StatusBox'

import ResourcesTable from 'components_new/organisms/ResourcesTable'

import { useIsMobile, useResources } from 'utils/hooks'
import { useMetrics } from 'redux/hooks/Metrics'
import { MetricStatus } from 'types/GlobalMetric'
import { useTranslation } from 'language/translator'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'

interface HomeOverviewProps {
  sx?: SxProps
}

const SEVEN_DAYS_AGO = new Date()

SEVEN_DAYS_AGO.setDate(new Date().getDate() - 7)
SEVEN_DAYS_AGO.setHours(0, 0, 0, 0)

const HomeOverview = (props: HomeOverviewProps) => {
  const { sx } = props

  const { t, lang } = useTranslation()
  const isMobile = useIsMobile()
  const { isAdmin } = useAccountSettings()
  const [metrics] = useMetrics()

  const [showMore, setShowMore] = useState(false)

  const isFetchingDashboards = useDashboardGroupsLoading()

  const favoriteDashboardGroups = useFavoriteDashboardGroups()
  const favoriteReports = useFavoriteReports()
  const recentDashboardGroups = useRecentDashboardGroups()
  const recentReports = useRecentReports()

  const favoriteResources = useResources({
    dashboardGroups: favoriteDashboardGroups,
    reports: favoriteReports
  })
  const recentResources = useResources(
    {
      dashboardGroups: recentDashboardGroups,
      reports: recentReports
    },
    'visitedAt'
  )

  const showFavorites = favoriteResources.length > 0
  const showRecents = recentResources.length > 0

  const newMetrics = useMemo(() => {
    if (!isAdmin) {
      // only show metrics for admins
      return []
    }

    return metrics
      .filter(
        (metric) =>
          metric.status === MetricStatus.INACTIVE &&
          metric.validated_at &&
          new Date(metric.validated_at) >= SEVEN_DAYS_AGO
      )
      .map((metric) => [
        <GenericListItem
          action="to"
          key={metric.id}
          size="medium"
          title={metric.title[lang]}
          to={`/metrics/${metric.id}`}
          variant="metric"
        />
      ])
  }, [isAdmin, metrics])

  return (
    <>
      {newMetrics.length > 0 ? (
        <>
          <StatusBox
            color="brand"
            severity="info"
            title={<NewMetricsTitle />}
            sx={{ mb: 2 }}
          >
            <Text variant="body1">{t('metrics', 'new_metrics_info_text')}</Text>
            <List dense={true}>{newMetrics.slice(0, 5)}</List>
            {newMetrics.length > 5 ? (
              <Button onClick={() => setShowMore(true)} variant="text">
                {t('metrics', 'show_all_x_metrics', false, {
                  count: newMetrics.length.toString()
                })}
              </Button>
            ) : null}
          </StatusBox>
          <SimpleDialog
            title={<NewMetricsTitle />}
            open={showMore}
            onClose={() => setShowMore(false)}
            actions={
              <Button onClick={() => setShowMore(false)} variant="text">
                {t('global', 'cancel')}
              </Button>
            }
          >
            <List dense={true}>{newMetrics}</List>
          </SimpleDialog>
        </>
      ) : null}

      {showFavorites || showRecents ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            position: 'relative',
            ...sx
          }}
        >
          {[
            ...(showFavorites
              ? [
                  {
                    context: Context.FROM_HOME_FAVORITES,
                    iconName: 'Favorite' as const,
                    loading: isFetchingDashboards,
                    resources: favoriteResources,
                    search: '?tab=favorites',
                    title: t('global', 'favorites'),
                    to: '/resources'
                  }
                ]
              : []),
            ...(showRecents
              ? [
                  {
                    context: Context.FROM_HOME_RECENT,
                    iconName: 'History' as const,
                    loading: isFetchingDashboards,
                    resources: recentResources,
                    title: t('home', 'recently_visited'),
                    to: '/resources'
                  }
                ]
              : [])
          ].map((item, i) => (
            <Box
              key={i}
              sx={{
                flex: 1
              }}
            >
              <SectionHeader
                color="secondary"
                divider={true}
                gutterBottom={true}
                icon={{ name: item.iconName }}
                search={item.search}
                title={item.title}
                to={item.to}
              />

              <ResourcesTable
                context={item.context}
                items={item.resources}
                loading={item.loading}
                preview={true}
              />
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  )
}

const NewMetricsTitle = React.memo(() => {
  const { t } = useTranslation()

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {t('metrics', 'new_metrics_title')}
      <Emoji name="tada" />
    </Box>
  )
})

export default HomeOverview
